import React, { useCallback, useMemo, useState } from 'react';
import { SlideMenu } from 'apps/sigma2-ui/src/components/SlideMenu';
import { FilterSearchBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterSearchBox';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { CreateOrUpdateDashboardChartDto } from 'libs/end-to-end/src/dashboard';
import { getFeaturesForNewChart } from 'apps/sigma2-ui/src/lib/charts/get-features-for-new-chart';
import { EditDashboardChartSlideMenu } from 'apps/sigma2-ui/src/components/EditDashboardChartSlideMenu';

interface DashboardChartOption {
  name: string;
  description: string;
  img: string;
  feature: FiltersFeatureName;
}

export const CreateDashboardChartSlideMenu = ({
  isOpen,
  setIsOpen,
  setSelectedFeature,
  selectedFeature = null,
}: {
  isOpen: boolean;
  setIsOpen: Setter<boolean>;
  selectedFeature: FiltersFeatureName | null;
  setSelectedFeature: Setter<FiltersFeatureName | null>;
}) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const language = useLanguage();

  const { myObservation } = useObservation();

  const enabledFeatures = useMemo(
    () => (myObservation ? getFeaturesForNewChart(myObservation) : []),
    [myObservation],
  );

  const sigma2Analysis = useMemo<DashboardChartOption[]>(() => {
    const analysis: DashboardChartOption[] = [];

    if (enabledFeatures.includes('progressPage')) {
      analysis.push({
        name: 'Disparity Trends',
        description: 'See changes over time',
        img: '/images/dashboards/trends.png',
        feature: 'progressPage',
      });
    }

    if (enabledFeatures.includes('timeSeriesTrendsPrimary')) {
      analysis.push({
        name: 'Outcome Trends',
        description: 'See trends for specific metrics given a date range',
        img: '/images/dashboards/metricTrends.png',
        feature: 'timeSeriesTrendsPrimary',
      });
    }

    if (enabledFeatures.includes('successFactors')) {
      analysis.push({
        name: 'Key Factors',
        description: `See the key factors that lead to a given outcome that you're researching`,
        img: '/images/dashboards/keyFactors.png',
        feature: 'successFactors',
      });
    }

    if (enabledFeatures.includes('exploreHeatmap')) {
      analysis.push({
        name: 'Controlled Disparities',
        description: 'A birdseye view of disparities in your data',
        img: '/images/dashboards/explore.png',
        feature: 'exploreHeatmap',
      });
    }

    if (enabledFeatures.includes('dataIntelligenceDeepDive')) {
      analysis.push({
        name: 'Disparity & Bias Breakdown',
        description: 'Explore disparity and bias in detail',
        img: '/images/dashboards/biasAnalysis.png',
        feature: 'dataIntelligenceDeepDive',
      });
    }

    if (enabledFeatures.includes('binScatterMultiSelect')) {
      analysis.push({
        name: 'Group trends',
        description:
          'Compare metrics for different groups to find disparities in your data.',
        img: '/images/dashboards/groupTrends.png',
        feature: 'binScatterMultiSelect',
      });
    }

    if (enabledFeatures.includes('employeeLifecycle')) {
      analysis.push({
        name: 'Lifecycle Flow',
        description: 'See how activity moves through stages.',
        img: '/images/dashboards/pipelineAnalysis.png',
        feature: 'employeeLifecycle',
      });
    }

    return analysis;
  }, [enabledFeatures]);

  const filteredSigma2Analysis = useMemo(() => {
    return sigma2Analysis.filter((item) =>
      `${item.name} ${item.description}`
        .toLowerCase()
        .includes(searchValue.toLowerCase()),
    );
  }, [searchValue, sigma2Analysis]);

  const essentialsAnalysis = useMemo<DashboardChartOption[]>(() => {
    const analysis: DashboardChartOption[] = [];

    if (enabledFeatures.includes('summaryStats')) {
      analysis.push({
        name: language.summaryStats(),
        description: language.summaryStatsDescription(),
        img: '/images/dashboards/summaryStatistic.png',
        feature: 'summaryStats',
      });
    }

    if (enabledFeatures.includes('tableView')) {
      analysis.push({
        name: language.dataTable(),
        description: language.dataTableDescription(),
        img: '/images/dashboards/dataTable.png',
        feature: 'tableView',
      });
    }

    if (enabledFeatures.includes('richText')) {
      analysis.push({
        name: language.richTextPageTitle(),
        description: language.richTextDescription(),
        img: '/images/dashboards/richText.png',
        feature: 'richText',
      });
    }

    if (enabledFeatures.includes('geolocation')) {
      analysis.push({
        name: language.geolocationPageTitle(),
        description: language.geolocationDescription(),
        img: '/images/dashboards/geolocation.png',
        feature: 'geolocation',
      });
    }

    if (enabledFeatures.includes('externalStreamlit')) {
      analysis.push({
        name: 'External Streamlit',
        description: 'Embed an external Streamlit application',
        img: '/images/dashboards/richText.png', // Reuse richText image for now
        feature: 'externalStreamlit',
      });
    }

    return analysis;
  }, [enabledFeatures, language]);

  const filteredEssentialsAnalysis = useMemo(() => {
    return essentialsAnalysis.filter((item) =>
      `${item.name} ${item.description}`
        .toLowerCase()
        .includes(searchValue.toLowerCase()),
    );
  }, [searchValue, essentialsAnalysis]);

  const [selectedItem, setSelectedItem] = useState<DashboardChartOption | null>(
    null,
  );

  const computedSelectedItem = useMemo(() => {
    if (selectedItem) {
      return selectedItem;
    }

    if (!selectedFeature) {
      return null;
    }

    return (
      [...sigma2Analysis, ...essentialsAnalysis].find(
        (a) => a.feature === selectedFeature,
      ) ?? null
    );
  }, [essentialsAnalysis, selectedFeature, selectedItem, sigma2Analysis]);

  const handleCloseEditSlide = useCallback(() => {
    setSelectedItem(null);
    setSelectedFeature(null);
  }, [setSelectedFeature]);

  const { addDraftChart, dashboardDraft } = useDashboards();

  const saveChart = useCallback(
    (dashboardChart: CreateOrUpdateDashboardChartDto) => {
      return addDraftChart(dashboardChart);
    },
    [addDraftChart],
  );

  return (
    <>
      <SlideMenu
        show={isOpen}
        onClose={() => setIsOpen(false)}
        qaDataName="slide-menu-create"
      >
        <div className="sticky top-0">
          <div className="-m-6 pt-6 p-3 bg-white">
            <FilterSearchBox
              value={searchValue}
              onChange={(newValue) => setSearchValue(newValue)}
            />
          </div>
        </div>
        <div className="pt-12 -mx-6 overflow-y-auto">
          <div className="flex flex-col">
            {filteredSigma2Analysis.length > 0 && (
              <div className="px-12 mb-2.5">
                <S2Typography
                  variant="body-7-bold"
                  textColorVariant="gray-1000"
                  translatedContent={language.sigma2Analysis()}
                />
              </div>
            )}
            {filteredSigma2Analysis.map((item) => (
              <div
                className="flex gap-4 p-2 px-12 hover:bg-gray-100 cursor-pointer min-h-24 place-items-center"
                key={item.name}
                data-testid={item.name}
                onClick={() => {
                  setSelectedItem(item);
                  setIsOpen(false);
                }}
              >
                <img className="w-16 h-16" src={item.img} />
                <div className="flex-col gap-4 ">
                  <S2Typography
                    variant="body-7"
                    textColorVariant="gray-1000"
                    translatedContent={language.dangerousUserInput({
                      value: item.name,
                    })}
                  />
                  <S2Typography
                    variant="body-7"
                    translatedContent={language.dangerousUserInput({
                      value: item.description,
                    })}
                  />
                </div>
              </div>
            ))}
            {filteredEssentialsAnalysis.length > 0 && (
              <div className="px-12 mb-2.5 mt-4">
                <S2Typography
                  variant="body-7-bold"
                  textColorVariant="gray-1000"
                  translatedContent={language.essentialsAnalysis()}
                />
              </div>
            )}
            {filteredEssentialsAnalysis.map((item) => (
              <div
                className="flex gap-4 p-2 px-12 hover:bg-gray-100 cursor-pointer min-h-24 place-items-center"
                key={item.name}
                data-testid={item.name}
                onClick={() => {
                  setSelectedItem(item);
                  setIsOpen(false);
                }}
              >
                <img className="w-16 h-16" src={item.img} />
                <div className="flex-col gap-4 ">
                  <S2Typography
                    variant="body-7"
                    textColorVariant="gray-1000"
                    translatedContent={language.dangerousUserInput({
                      value: item.name,
                    })}
                  />
                  <S2Typography
                    variant="body-7"
                    translatedContent={language.dangerousUserInput({
                      value: item.description,
                    })}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </SlideMenu>
      {computedSelectedItem !== null && dashboardDraft && (
        <EditDashboardChartSlideMenu
          isOpen={computedSelectedItem !== null}
          setIsOpen={handleCloseEditSlide}
          feature={computedSelectedItem.feature}
          saveChart={saveChart}
          dashboardId={dashboardDraft.dashboardId}
          dashboard={dashboardDraft.draft}
        />
      )}
    </>
  );
};
